import { getCalculatedInvoiceStatus } from '@app/logic/invoices';
import { useInvoiceList } from '@app/services/hooks/invoice/useInvoiceList';
import { usePageScrolling, useUrlPagination } from '@app/utils/hooks';
import { useInvoicesListTable } from '@app/utils/hooks/invoices/useInvoicesListTable';
import { useTableColumnsConfiguration } from '@app/utils/hooks/table/useTableColumnsConfiguration';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { getInvoiceStatusColor } from '@app/utils/status';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../various/EmptyState';
import { PageTitle } from '../various/PageTitle';
import { Pagination } from '../various/Pagination';
import { Table } from '../various/Table';
import { TableControls } from '../various/TableControls';

import { InvoicesListCardSkeleton } from './InvoicesList/InvoicesListCardSkeleton';
import { InvoicesListCard } from './InvoicesList';

export const Invoices = () => {
  const { t } = useTranslation(['common', 'invoices', 'totals', 'orders']);
  const { columnsVisibility } = useTableColumnsConfiguration({ tableName: 'invoicesTable' });
  const [page, setPage] = useUrlPagination();

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    scrollToTopDelay();
  };

  const { scrollToTopDelay } = usePageScrolling();
  const { data, isLoading: isLoadingInitialQuery, isPlaceholderData, isError } = useInvoiceList({ page });
  const isLoading = isLoadingInitialQuery || isPlaceholderData;
  const invoices = React.useMemo(() => data?.invoices ?? [], [data]);
  const invoicesTotalCount = data?.invoicesTotalCount ?? 0;

  const { table } = useInvoicesListTable({ handlePageChange, invoices, page });

  const shouldShowEmptyState = isDefined(invoices) && isEmpty(invoices) && !isLoading && !isError;

  return (
    <PageTitle title={t('common:invoice_other')}>
      <div className="flex flex-1 flex-col px-4 sm:px-8">
        <div className="sticky top-[--headerHeight] z-topBar mb-2 flex flex-col gap-2 bg-neutral-96 py-2 transition-all group-[.scrollingDown]/body:top-0 lg:mb-0">
          <div className="flex items-center gap-8">
            <h2 className="flex-1 heading-3 sm:flex-initial">
              {t('common:invoice_other')}
              <span className="text-neutral-40"> ({invoicesTotalCount})</span>
            </h2>
            {!shouldShowEmptyState && <TableControls table={table} columnsConfiguration={columnsVisibility} tableName="invoicesTable" />}
          </div>
        </div>
        {shouldShowEmptyState && <EmptyState dataTestId="invoiceListEmptyState" title={t('invoices:no_invoices')} />}
        {isError && <EmptyState title={t('invoices:no_invoices_found')} />}
        {(isLoading || !isEmpty(invoices)) && (
          <>
            <Table
              table={table}
              isLoading={isLoading}
              getRowMarkerColor={row => getInvoiceStatusColor(getCalculatedInvoiceStatus(row.original))}
              renderSkeletonCard={index => <InvoicesListCardSkeleton key={index} />}
              renderCard={row => <InvoicesListCard row={row} />}
              dataTestId="invoiceList"
            />
            <Pagination onChange={table.setPageIndex} current={page} totalItems={invoicesTotalCount} isHidden={isLoadingInitialQuery} />
          </>
        )}
      </div>
    </PageTitle>
  );
};
